import { createContext, type FC, type ReactNode, useContext, useMemo, useState } from 'react';

import type { AnalysisPlatform } from '@/shared/api/types/generated';
import {
  type AnalysisSegment,
  EMPTY_SEGMENT,
  getLockedDimensions,
  isSegmentActive,
  type LockedDimension,
} from '@/shared/utils/analysis/segment';

// ============================================================================
// Contexte du segment global d'une analyse — porté par la page de détail,
// consommé par le bandeau, les trois onglets et le drawer de niche (qui en
// hérite donc automatiquement).
// ============================================================================

interface AnalysisSegmentContextValue {
  segment: AnalysisSegment;
  isActive: boolean;
  lockedDimensions: LockedDimension[];
  platform: AnalysisPlatform;
  updateSegment: (partial: Partial<AnalysisSegment>) => void;
  resetSegment: () => void;
}

const INERT_VALUE: AnalysisSegmentContextValue = {
  segment: EMPTY_SEGMENT,
  isActive: false,
  lockedDimensions: [],
  platform: 'vinted',
  updateSegment: () => {},
  resetSegment: () => {},
};

const AnalysisSegmentContext = createContext<AnalysisSegmentContextValue>(INERT_VALUE);

export interface AnalysisSegmentProviderProps {
  /** Filtre de création (ids) — source des dimensions verrouillées (`parsedFilter` sur Vinted,
   * `vestiaireFilter` sur VC — cf `analysisCriteriaFilter`). */
  lockFilter: Record<string, unknown> | null | undefined;
  platform: AnalysisPlatform;
  children: ReactNode;
}

export const AnalysisSegmentProvider: FC<AnalysisSegmentProviderProps> = ({ lockFilter, platform, children }) => {
  const [segment, setSegment] = useState<AnalysisSegment>(EMPTY_SEGMENT);

  const lockedDimensions = useMemo(() => getLockedDimensions(lockFilter), [lockFilter]);

  const value = useMemo<AnalysisSegmentContextValue>(
    () => ({
      segment,
      isActive: isSegmentActive(segment),
      lockedDimensions,
      platform,
      updateSegment: (partial) => setSegment((prev) => ({ ...prev, ...partial })),
      resetSegment: () => setSegment(EMPTY_SEGMENT),
    }),
    [segment, lockedDimensions, platform],
  );

  return <AnalysisSegmentContext.Provider value={value}>{children}</AnalysisSegmentContext.Provider>;
};

/** Hors provider (ex. composant réutilisé ailleurs) : segment inerte vide. */
export const useAnalysisSegment = (): AnalysisSegmentContextValue => useContext(AnalysisSegmentContext);
